import {Assert, Clone, CopyText} from "js-vextensions";
import {GenerateUUID, observer_mgl} from "mobx-graphlink";
import moment from "moment";
import React, {useState} from "react";
import {Button, CheckBox, Column, Row, Select, Spinner, Text, TextInput, TimeSpanInput} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {HSLA, InfoButton, observer_simple, RunInAction, RunInAction_Set, VDateTime} from "web-vcore";
import {UpdateJournalEntry} from "../../../Server/Commands/JournalEntryCommands.js";
import {JournalEntry, JournalSegment, JournalSegment_HideableField, JournalSegment_HideableField_values} from "../../../Server/DB/journalEntries/@JournalEntry.js";
import {store} from "../../../Store/index.js";
import {ACTSetCopiedData, CopiedData} from "../../../Store/main/general/CopiedData.js";
import {ClarityUI, MappingProgressUI, SegmentTextTab, TechExecutionUI} from "../../../Store/main/journal.js";
import {Clone_KeepTypeDef} from "../../../Utils/General/General.js";
import {StarsRating} from "../../../Utils/ReactComponents/StarsRating.js";
import {SegmentCleanupUI} from "./SegmentUI/SegmentSimplifyUI.js";
import {SegmentSummarizeUI} from "./SegmentUI/SegmentSummarizeUI.js";
import {SegmentTextTab_LongText} from "./SegmentUI/TextTabs/LongText.js";
import {SegmentTextTab_PublicText} from "./SegmentUI/TextTabs/PublicText.js";
import {SegmentTextTab_ShortText} from "./SegmentUI/TextTabs/ShortText.js";
import {GetSegmentTextWordCounts} from "./SegmentUI/TextTabs/@Shared.js";
import {TextTransformToolsUI} from "./SegmentUI/TextTransformDialog.js";
import {EstimateSegmentStartTime} from "../../../Server/DB/@Shared/JournalTimeEstimates.js";

interface SharedProps {
	entry: JournalEntry;
}

/** The three text fields a segment has. Only one is shown at a time (see `SegmentTextTab`), because
 * showing all three stacked made the segment panel unwieldy.
 *
 * The option list is built per-render (rather than as a module constant) so each button can carry its
 * field's word count, eg. "Public text (135)". */
function GetSegmentTextTabOptions(wordCounts: {shortText: number, longText: number, publicText: number}) {
	return [
		{name: `Short text (${wordCounts.shortText})`, value: SegmentTextTab.shortText},
		{name: `Long text (${wordCounts.longText})`, value: SegmentTextTab.longText},
		{name: `Public text (${wordCounts.publicText})`, value: SegmentTextTab.publicText},
	];
}

type JournalSegmentUIProps = {segment: JournalSegment, segments: JournalSegment[], showActionsRow: boolean, showStartTime?: boolean|n} & SharedProps;
export const JournalSegmentUI = observer_mgl((props: JournalSegmentUIProps)=>{
	const {segment, segments, entry, showActionsRow, showStartTime} = props;
	const uiState = store.main.journal;
	const [summarizeMode, setSummarizeMode] = useState(false);
	const [cleanupMode, setCleanupMode] = useState(false);
	const [longTextCursorTimestamp, setLongTextCursorTimestamp] = useState<number|null>(null);

	const transcribeState = store.main.settings.transcribe;
	const timeSyncActive = transcribeState.timeSyncActive;
	const remoteState = transcribeState.remoteCursorState;
	const effectiveWallClockTime = timeSyncActive && remoteState.entryId == entry.id && remoteState.segmentId == segment.id
		? remoteState.timestamp
		: longTextCursorTimestamp;

	//const inJourneyUI = !showEntitiesSequence;
	const startTime_estimated = showStartTime ? EstimateSegmentStartTime(entry, segment) : null;
	const segmentIndex = segments.indexOf(segment);
	Assert(segmentIndex != -1, "Segment not found in entry's segments.");

	// word counts shown in the text-tab buttons (recomputed when the segment's text changes)
	const segmentTextWordCounts = GetSegmentTextWordCounts(segment);

	// if clarity doesn't match any of the star-values, force it to show as a percent (since otherwise the ui can't display the real value)
	const clarity_final = segment.clarity || 0;
	const clarityUI = !clarity_final.IsMultipleOf(.2, .001) ? ClarityUI.percent : uiState.clarityUI;

	// if tech-execution doesn't match any of the star-values, force it to show as a percent (since otherwise the ui can't display the real value)
	const techExecution_final = segment.techExecution_preSleep || 0;
	const techExecutionUI = !techExecution_final.IsMultipleOf(.2, .001) ? TechExecutionUI.percent : uiState.techExecutionUI;

	// if mapping-progress doesn't match any of the star-values, force it to show as a percent (since otherwise the ui can't display the real value)
	const mappingProgress_final = segment.mappingProgress || 0;
	const mappingProgressUI = !mappingProgress_final.IsMultipleOf(.2, .001) ? MappingProgressUI.percent : uiState.mappingProgressUI;

	const UpdateSegment = (changerFunc: (data: JournalSegment)=>any)=>{
		const segments_new = Clone_KeepTypeDef(entry.segments);
		const segment_newData = segments_new[segmentIndex];
		changerFunc(segment_newData);
		new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
	};

	const segmentLength = segment.length || 0;
	const showOpt = (fieldName: JournalSegment_HideableField)=>!uiState.segmentFieldsToHide.includes(fieldName) || uiState.segmentFields_showAll;

	return (
		<>
			{showStartTime &&
			<Row center>
				<Text>Start time (est.): {startTime_estimated ? moment(startTime_estimated).format("YYYY-MM-DD HH:mm:ss") : "n/a"}</Text>
			</Row>}
			<Row center mt={segmentIndex == 0 ? 0 : 3} pl={5} style={{background: HSLA(0, 0, 1, .3), borderRadius: "5px 5px 0 0", flexWrap: "wrap", gap: 5}}>
				<Row>
					<CheckBox text="Lucid" value={segment.lucid} onChange={val=>{
						const segments_new = Clone_KeepTypeDef(entry.segments);
						segments_new[segmentIndex] = segment.Extended({lucid: val});
						new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
					}}/>
					<InfoButton ml={5} text={`
						If you knew you were dreaming for at least part of the segment. (ideally, extract lucid periods into their own segments)
						Precisely: You were "lucid" if you knew (or assumed) that the world was internal to you. (ie. neither real, nor an [external] simulation)
					`.AsMultiline(0)}/>
				</Row>
				{showOpt("semiLucid") && <Row>
					<CheckBox text="Near-lucid" value={segment.semiLucid} onChange={val=>{
						const segments_new = Clone_KeepTypeDef(entry.segments);
						segments_new[segmentIndex] = segment.Extended({semiLucid: val});
						new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
					}}/>
					<InfoButton ml={5} text={`
						If not a lucid (see definition), but "felt like one", and had a similar outcome. (eg. where you can see how someone might call it one)
						Examples: Knowing it's not real life (without knowing it's a dream/internal); knowing/using major control/powers; remembering dream goals.
					`.AsMultiline(0)}/>
				</Row>}
				{showOpt("ordered") && <Row>
					<CheckBox text="Ordered" value={segment.ordered} onChange={val=>{
						const segments_new = Clone_KeepTypeDef(entry.segments);
						segments_new[segmentIndex] = segment.Extended({ordered: val});
						new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
					}}/>
					<InfoButton ml={5} text={`
						Mark true if:
						* The order of events in the short-text (events separated by ";" or new-line) matches that of the actual dream, and...
						* Each such event is unique, ie. given perfect memory of the dream, you could reliably know which of two randomly selected events happened after.
						Note: A dream-segment must be "ordered" for it to be included in the dream-quiz system, when using quiz type "comesAfter".
					`.AsMultiline(0)}/>
				</Row>}
				{showOpt("llmTools") && <>
					<CheckBox text="LT->ST" enabled={segment.longText.trim().length > 0} style={{fontSize: 12}}
						value={summarizeMode} onChange={val=>setSummarizeMode(val)}/>
					<CheckBox text="Cleanup" enabled={segment.longText.trim().length > 0} style={{fontSize: 12}}
						value={cleanupMode} onChange={val=>setCleanupMode(val)}/>
				</>}
				{showOpt("textTransforms") && <Button mdIcon="tools" title="Text-transform tools" onClick={()=>{
					ShowMessageBox({
						title: "Text-transform tools",
						message: ()=><TextTransformToolsUI/>,
					});
				}}/>}
				{showOpt("length") && <Row>
					<Text>Length:</Text>
					<InfoButton ml={5} text="If the length specified includes unlisted events, please add placeholders (eg: [...]), so the listed events aren't misunderstood as spanning the whole duration."/>
					<TimeSpanInput ml={5} style={{width: 50}} largeUnit="minute" smallUnit="second" value={segmentLength} onChange={totalSeconds=>UpdateSegment(a=>a.length = totalSeconds)}/>
				</Row>}
				{showOpt("wakeTime") && <Row>
					<Text mr={5}>Wake:</Text>
					<VDateTime dateFormat="YYYY-MM-DD" timeFormat="HH:mm:ss" value={segment.wakeTime ? moment(segment.wakeTime) : null} onChange={val=>{
						UpdateSegment(a=>a.wakeTime = val?.valueOf() ?? null);
					}}/>
				</Row>}
				{showOpt("clarity") && <Row>
					<Text>Clarity:</Text>
					<InfoButton ml={5} text={`
						Richness/clarity of thoughts and perceptions, relative to your typical awake state. (eg. taking a stroll)

						General guidelines: (if descriptions don't match the given clarity-% for you, feel free to ignore)
						0 stars (0%): Completely unconscious (if exactly 0, field considered unspecified)
						1 stars (20%): Base non-lucid
						2 stars (40%): Enhanced non-lucid, or base lucid
						3 stars (60%): Enhanced lucid (eg. remembering goals)
						4 stars (80%): Enhanced lucid, taking charge
						5 stars (100%): Enhanced lucid, taking charge, life-like
						100+%: Lucid, and even richer/clearer than your typical awake state
						`.AsMultiline(0)}/>
					{clarityUI == ClarityUI.percent &&
					<Spinner ml={5} min={0} max={500} value={(clarity_final * 100).RoundTo(.01)} onChange={val=>UpdateSegment(a=>a.clarity = val / 100)}/>}
					{clarityUI == ClarityUI.stars &&
					<StarsRating titleFunc_attribute="clarity" value={clarity_final * 5} onChange={val=>UpdateSegment(a=>a.clarity = val != null ? val / 5 : null)}/>}
					<Button ml={3} p="3px 5px" text={clarityUI == ClarityUI.percent ? "%" : "S"} title={`Showing clarity as: ${ClarityUI[clarityUI]}`}
						onClick={()=>{
							RunInAction("JournalSegmentUI.clarityForm.onChange", ()=>uiState.clarityUI = clarityUI == ClarityUI.percent ? ClarityUI.stars : ClarityUI.percent);
							if (!clarity_final.IsMultipleOf(.2, .001)) {
								//UpdateSegment(a=>a.clarity = clarity_final.RoundTo(.2).KeepBetween(.2, 1));
								UpdateSegment(a=>a.clarity = clarity_final.RoundTo(.2).KeepBetween(0, 1));
							}
						}}/>
				</Row>}
				{showOpt("excludeFromIntegration") && <>
					<Row>
						<CheckBox text="No integration" value={segment.excludeFromIntegration ?? false} onChange={val=>UpdateSegment(a=>a.excludeFromIntegration = val)}/>
						<InfoButton ml={5} text="Excludes this segment from integration in engine components like dream-recall, dream-quiz, etc."/>
					</Row>
					{showOpt("excludeFromIntegration_reason") && segment.excludeFromIntegration && <Row>
						<Text>Reason:</Text>
						<TextInput ml={5} value={segment.excludeFromIntegration_reason} onChange={val=>UpdateSegment(a=>a.excludeFromIntegration_reason = val)}/>
					</Row>}
				</>}
				{showOpt("techExecution_preSleep") && <Row>
					<Text>Tech-exec:</Text>
					<InfoButton ml={5} text={`
						The quality of the techniques performed just before falling asleep, before this dream segment.
						(To describe tech-exec quality of initial driftoff, assign this to the first dream segment.)
					`.AsMultiline(0)}/>
					{techExecutionUI == TechExecutionUI.percent &&
					<Spinner ml={5} min={0} max={100} value={(techExecution_final * 100).RoundTo(.01)} onChange={val=>UpdateSegment(a=>a.techExecution_preSleep = val / 100)}/>}
					{techExecutionUI == TechExecutionUI.stars &&
					<StarsRating titleFunc_attribute="tech-exec quality" value={techExecution_final * 5} onChange={val=>UpdateSegment(a=>a.techExecution_preSleep = val != null ? val / 5 : null)}/>}
					<Button ml={3} p="3px 5px" text={techExecutionUI == TechExecutionUI.percent ? "%" : "S"} title={`Showing tech-exec quality as: ${TechExecutionUI[techExecutionUI]}`}
						onClick={()=>{
							RunInAction("JournalSegmentUI.techExecution.onChange", ()=>uiState.techExecutionUI = techExecutionUI == TechExecutionUI.percent ? TechExecutionUI.stars : TechExecutionUI.percent);
							if (!techExecution_final.IsMultipleOf(.2, .001)) {
								UpdateSegment(a=>a.techExecution_preSleep = techExecution_final.RoundTo(.2).KeepBetween(0, 1));
							}
						}}/>
				</Row>}
				{showOpt("mappingProgress") && <Row>
					<Text>Mapping:</Text>
					<InfoButton ml={5} text={`
						How much progress has been made on mapping this dream's contents onto the dream-world map.
					`.AsMultiline(0)}/>
					{mappingProgressUI == MappingProgressUI.percent &&
					<Spinner ml={5} min={0} max={500} value={(mappingProgress_final * 100).RoundTo(.01)} onChange={val=>UpdateSegment(a=>a.mappingProgress = val / 100)}/>}
					{mappingProgressUI == MappingProgressUI.stars &&
					<StarsRating titleFunc_attribute="mapping-progress" value={mappingProgress_final * 5} onChange={val=>UpdateSegment(a=>a.mappingProgress = val != null ? val / 5 : null)}/>}
					<Button ml={3} p="3px 5px" text={mappingProgressUI == MappingProgressUI.percent ? "%" : "S"} title={`Showing mapping-progress as: ${MappingProgressUI[mappingProgressUI]}`}
						onClick={()=>{
							RunInAction("JournalSegmentUI.mappingProgress.onChange", ()=>uiState.mappingProgressUI = mappingProgressUI == MappingProgressUI.percent ? MappingProgressUI.stars : MappingProgressUI.percent);
							if (!mappingProgress_final.IsMultipleOf(.2, .001)) {
								UpdateSegment(a=>a.mappingProgress = mappingProgress_final.RoundTo(.2).KeepBetween(0, 1));
							}
						}}/>
				</Row>}
				{showOpt("id") && <Row>
					<Text sel>ID: {segment.id}</Text>
					<Button mdIcon="refresh" ml={5} onClick={()=>{
						const proceed = ()=>{
							UpdateSegment(a=>a.id = GenerateUUID());
						};
						if (segment.id == null) proceed();
						else {
							ShowMessageBox({
								title: "Overwrite segment ID?", cancelButton: true,
								message: `Overwrite this segment's ID?`,
								onOK: ()=>proceed(),
							});
						}
					}}/>
				</Row>}
				<Button ml={5} mdIcon="content-copy" title="Copy segment's id/path" enabled={segment.id != null} onClick={()=>{
					ACTSetCopiedData(new CopiedData({journalPath: {entryId: entry.id, segmentId: segment.id!}}));
					const idPath = `${entry.id}/${segment.id}`;
					CopyText(idPath); // also copy to clipboard (probably make this a setting later) [reason: for copy-pasting between dev and prod domains]
				}}/>
				<Button text="More" onClick={()=>{
					ShowMessageBox({
						title: "Dream fields to show",
						message: observer_simple(()=>{
							return (
								<Column style={{gap: 5}}>
									{JournalSegment_HideableField_values.map(fieldName=>{
										return (
											<CheckBox key={fieldName} text={fieldName} value={!uiState.segmentFieldsToHide.includes(fieldName)}
												onChange={val=>{
													if (val) RunInAction_Set(()=>uiState.segmentFieldsToHide = uiState.segmentFieldsToHide.Exclude(fieldName));
													else RunInAction_Set(()=>uiState.segmentFieldsToHide.push(fieldName));
												}}/>
										);
									})}
								</Column>
							);
						}),
					})
				}}/>
				<CheckBox text="All" value={uiState.segmentFields_showAll} onChange={val=>RunInAction_Set(()=>uiState.segmentFields_showAll = val)}/>
			</Row>
			{summarizeMode &&
				<SegmentSummarizeUI entry={entry} segment={segment} segmentIndex={segmentIndex}/>}
			{cleanupMode &&
				<SegmentCleanupUI entry={entry} segment={segment} segmentIndex={segmentIndex}/>}
			{/* Tab bar selecting which of the segment's three text fields is displayed below. */}
			<Row center mt={3} mb={3} style={{gap: 10, flexWrap: "wrap"}}>
				<Select displayType="button bar" options={GetSegmentTextTabOptions(segmentTextWordCounts)} value={uiState.segmentTextTab}
					onChange={val=>RunInAction_Set(()=>uiState.segmentTextTab = val)}/>
				{uiState.segmentTextTab == SegmentTextTab.publicText &&
				<InfoButton text={`
					The dream report as you posted it publicly (bulleted markdown).
					This is the ONLY text Echo Hunt's corpus is built from -- it never reads the long-text.

					Expected format:
					* "# First lucid" groups the night into dreams. (one segment = one dream; only the first group is used)
					* "### Section title" heads a scene, and becomes each bullet's "heading".
					* "* " lines are the bullets; an INDENTED bullet is merged into its parent.
					* Ordinary prose paragraphs are context only, never bullets.
				`.AsMultiline(0)}/>}
			</Row>
			{uiState.segmentTextTab == SegmentTextTab.shortText &&
				<SegmentTextTab_ShortText entry={entry} segment={segment} segmentIndex={segmentIndex}/>}
			{uiState.segmentTextTab == SegmentTextTab.longText &&
				<SegmentTextTab_LongText entry={entry} segment={segment} segmentIndex={segmentIndex}
					effectiveWallClockTime={effectiveWallClockTime} onCursorTimestampChange={setLongTextCursorTimestamp}/>}
			{uiState.segmentTextTab == SegmentTextTab.publicText &&
				<SegmentTextTab_PublicText entry={entry} segment={segment} segmentIndex={segmentIndex}/>}
			{showActionsRow &&
			<Row ml={5}>
				<Text>Actions:</Text>
				<Button ml={5} p="0 10px" text="Add above" onClick={()=>{
					const segments_new = Clone_KeepTypeDef(entry.segments);
					segments_new.Insert(segmentIndex, new JournalSegment());
					new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
				}}/>
				<Button ml={5} p="0 10px" text="Add below" onClick={()=>{
					const segments_new = Clone_KeepTypeDef(entry.segments);
					segments_new.Insert(segmentIndex + 1, new JournalSegment());
					new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
				}}/>
				<Button ml={5} mdIcon="arrow-up-bold" enabled={segmentIndex > 0} onClick={()=>{
					const segments_new = Clone_KeepTypeDef(entry.segments);
					segments_new[segmentIndex - 1] = Clone(segment);
					segments_new[segmentIndex] = Clone_KeepTypeDef(entry.segments[segmentIndex - 1]);
					new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
				}}/>
				<Button ml={5} mdIcon="arrow-down-bold" enabled={segmentIndex < entry.segments.length - 1} onClick={()=>{
					const segments_new = Clone_KeepTypeDef(entry.segments);
					segments_new[segmentIndex + 1] = Clone(segment);
					segments_new[segmentIndex] = Clone_KeepTypeDef(entry.segments[segmentIndex + 1]);
					new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
				}}/>
				<Button ml={5} mdIcon="delete" onClick={()=>{
					ShowMessageBox({
						title: `Delete this segment`, cancelButton: true,
						message: `Delete this segment?`,
						onOK: ()=>{
							const segments_new = Clone_KeepTypeDef(entry.segments);
							segments_new.RemoveAt(segmentIndex);
							new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
						},
					});
				}}/>
			</Row>}
		</>
	);
});
